import { ROLES } from '@/shared/auth/constants'
import { useHasRole } from '@/shared/auth/hooks/use-has-role'
import { useCurrentSKU, useTenantFeatures } from '@/shared/auth/store'

import type { PageType } from '../types'

/**
 * Permission gate for surfacing AIR® AI affordances on a given page.
 *
 * Every surface — `case_detail` included — first requires the tenant-level
 * `beta_enable_ask_noah` feature flag (from `TenantDetails.features`). Tenants
 * without it never see Ask Noah, regardless of role or SKU. This deliberately
 * supersedes the old behavior where `case_detail` short-circuited to `true`
 * before any check. A missing/undefined `features` payload (in-flight query,
 * older backend) also gates to `false` — fail closed.
 *
 * Past the flag, `case_detail` skips only the search gate: the case-detail
 * route guard already authorized the user to read the case, so we don't
 * re-check authorization here. Every other page that surfaces "Ask Noah" needs
 * the SearchLogs gate — Analyst role + `is_search_enabled` SKU — which mirrors
 * the route guard at `src/routes/_dashboard/search.index.tsx:43-56`. There is
 * no generic `useHasPermission` in this codebase, so this is the canonical
 * composition.
 *
 * Centralizing it here keeps the two `AskAIButton` call sites (and any future
 * surfaces) from drifting — if case-detail's upstream gating is ever loosened
 * we can tighten this hook in one place instead of every button.
 *
 * **No demo gate here.** ADR 0015 dec. 4 reverses ADR 0006 dec. 5: the public
 * demo no longer hides Noah, it *constrains* it (locked composer, fixed prompt
 * chips, canned SSE). The demo world already grants `beta_enable_ask_noah` +
 * `is_search_enabled` and the demo persona is an Analyst, so both surfaces pass
 * the normal checks — constraint lives in the UI (`isDemoPresentation` in the
 * composer / empty state), not in this permission gate.
 *
 * @param pageType Surface key — `'case_detail'` requires only the feature
 *                 flag; all other values additionally require Analyst role +
 *                 active search SKU
 */
export function useCanUseAI(pageType: PageType | 'search'): boolean {
	const isAnalyst = useHasRole(ROLES.Analyst)
	const sku = useCurrentSKU()
	const features = useTenantFeatures()
	const searchEnabled = Boolean(sku?.is_search_enabled)

	if (!features?.beta_enable_ask_noah) return false

	if (pageType === 'case_detail') return true

	return isAnalyst && searchEnabled
}
